/**
 * VVTable — DataTable for V&V recommendations. CAL + effort filters
 * via the shared FilterDrawer. Pixel-width schema → auto-expanding
 * table with horizontal scroll (same treatment as Step 5 attack
 * paths, since V&V has 12 wide columns).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
} from '../../../../components/ui/DataTable';
import FilterDrawer from '../../../../components/ui/DataTable/FilterDrawer';
import {
  VV_COLUMNS_READ,
  CAL_LEVELS,
  EFFORT_LEVELS,
  type VVRecord,
} from './vvFieldSchema';
import styles from './Step8Tables.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const FilterDrawerAny = FilterDrawer as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

interface SortState { column: string; direction: 'asc' | 'desc'; }

export interface VVTableProps {
  recommendations: VVRecord[];
  loading?: boolean;
  emptyMessage?: string;
}

function sortRows(rows: VVRecord[], sort: SortState | null): VVRecord[] {
  if (!sort) return rows;
  const dir = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = (a as Record<string, unknown>)[sort.column];
    const bv = (b as Record<string, unknown>)[sort.column];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return String(av).localeCompare(String(bv)) * dir;
  });
}

function searchRows(rows: VVRecord[], query: string): VVRecord[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    Object.values(r).some((v) =>
      v == null
        ? false
        : Array.isArray(v)
        ? v.join(' ').toLowerCase().includes(q)
        : String(v).toLowerCase().includes(q),
    ),
  );
}

export default function VVTable({
  recommendations,
  loading,
  emptyMessage = 'No V&V recommendations yet.',
}: VVTableProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [calValues, setCalValues] = useState<string[]>([]);
  const [effortValues, setEffortValues] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const calCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of CAL_LEVELS) counts[c.value] = 0;
    for (const r of recommendations) {
      const k = String(r.cal_level ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [recommendations]);

  const effortCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const e of EFFORT_LEVELS) counts[e.value] = 0;
    for (const r of recommendations) {
      const k = String(r.estimated_effort ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [recommendations]);

  const filtered = useMemo(() => {
    let rows = recommendations;
    if (calValues.length > 0) {
      rows = rows.filter((r) => calValues.includes(String(r.cal_level ?? '')));
    }
    if (effortValues.length > 0) {
      rows = rows.filter((r) => effortValues.includes(String(r.estimated_effort ?? '')));
    }
    return rows;
  }, [recommendations, calValues, effortValues]);

  const searched = useMemo(() => searchRows(filtered, search), [filtered, search]);
  const sorted = useMemo(() => sortRows(searched, sort), [searched, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);

  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  const keyed = useMemo(
    () => paged.map((r, i) => ({ ...r, _rowKey: `${r.vv_id ?? 'row'}::${(page - 1) * pageSize + i}` })),
    [paged, page, pageSize],
  );

  const calFilter = {
    key: 'cal_level',
    label: 'CAL',
    values: calValues,
    onChange: (next: string[]) => { setCalValues(next); setPage(1); },
    options: CAL_LEVELS.map((c) => ({ value: c.value, label: c.label, count: calCounts[c.value] ?? 0 })),
  };
  const effortFilter = {
    key: 'estimated_effort',
    label: 'Effort',
    values: effortValues,
    onChange: (next: string[]) => { setEffortValues(next); setPage(1); },
    options: EFFORT_LEVELS.map((e) => ({ value: e.value, label: e.label, count: effortCounts[e.value] ?? 0 })),
  };

  return (
    <div className={styles.tableShell}>
      <FilterDrawerAny
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={[calFilter, effortFilter]}
      />
      <DataTableAny
        columns={VV_COLUMNS_READ}
        data={keyed}
        rowKey="_rowKey"
        sort={sort}
        onSortChange={(column: string | null, direction: 'asc' | 'desc' | null) =>
          setSort(column && direction ? { column, direction } : null)
        }
        loading={loading}
        emptyMessage={emptyMessage}
        toolbar={
          <TableToolbarAny
            search={{
              value: search,
              onChange: (v: string) => { setSearch(v); setPage(1); },
              placeholder: 'Search V&V recommendations…',
            }}
            count={{ value: sorted.length, label: 'Recommendations' }}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || calValues.length > 0 || effortValues.length > 0}
          />
        }
        pagination={
          <TablePaginationAny
            currentPage={page}
            totalItems={sorted.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => { setPageSize(size); setPage(1); }}
          />
        }
      />
    </div>
  );
}
